import './CaseStudyPart.scss'

import React, { FunctionComponent } from 'react'
import PuxMediaSelector, {
  PuxMediaSelectorType,
} from 'Root/Shared/components/content/mediaImage/PuxMediaSelector'
import { getLocalizedData } from 'Root/Shared/utils/localeURL'
import PuxIcon from 'Shared/components/PuxIcon/PuxIcon'
import PuxStructuredData from 'Shared/components/PuxStructuredData/PuxStructuredData'
import PuxWysiwyg, {
  PuxWysiwygHtmlBodyType,
} from 'Shared/components/PuxWysiwyg/PuxWysiwyg'
import { PuxKeyValueType } from 'Shared/queries/page-queries'
import { getLocalizedUrl } from 'Shared/utils/localeURL'
import PuxLink from 'Shared/utils/PuxLink'
import { t } from 'ttag'

import PuxButton from '../../PuxButton/PuxButton'

const localizedConfigData = getLocalizedData(
  process.env.LOCALE ?? ``,
  process.env.PROJECT ?? ``
)
const activeEnv = process.env.GATSBY_ACTIVE_ENV ?? ``

export interface CaseStudyPartType {
  contentType: 'EasySoftwareCaseStudy'
  contentItemId: string
  path: string
  displayText: string
  createdUtc: Date
  caseStudyPerex: PuxWysiwygHtmlBodyType
  caseStudyImage: PuxMediaSelectorType
  caseStudyParameters: {
    contentItems: [PuxKeyValueType]
  }
  caseStudyFilterParameters?: {
    contentItems: PuxKeyValueType[]
  }
  caseStudyIndustry?: string
  caseStudyCountry?: string
  caseStudyCompanySize?: string
}

function getCaseStudyJson(props: CaseStudyPartType) {
  const baseUrl = localizedConfigData.domains[activeEnv]
  const fullUrl = baseUrl + getLocalizedUrl(props.path)
  const productName = `Easy8`

  // Clean HTML from perex
  const cleanPerex = props.caseStudyPerex?.html?.replace(/<[^>]*>/g, ``) || ``

  // Get image URL
  const imageUrl =
    props.caseStudyImage?.mediaSelectorImage?.resizePaths?.[0] ||
    props.caseStudyImage?.mediaSelectorImage?.resizePathsFallback?.[0] ||
    ``
  const fullImageUrl = imageUrl.startsWith(`http`)
    ? imageUrl
    : `${baseUrl}${imageUrl}`

  // Format date
  const date = new Date(props.createdUtc)
  const datePublished = date.toISOString().split(`T`)[0]

  // Build article body from parameters and perex
  let articleBody = cleanPerex
  if (props.caseStudyParameters?.contentItems) {
    const parametersText = props.caseStudyParameters.contentItems
      .map((param) => {
        const value = param.keyValueContent?.html?.replace(/<[^>]*>/g, ``) || ``
        return `${param.displayText}: ${value}`
      })
      .join(`. `)
    articleBody = `${parametersText}. ${cleanPerex}`
  }

  return {
    '@context': `https://schema.org`,
    '@type': `Article`,
    '@id': fullUrl,
    mainEntityOfPage: {
      '@type': `WebPage`,
      '@id': fullUrl,
    },
    headline: props.displayText,
    description: cleanPerex,
    articleBody: articleBody,
    author: {
      '@type': `Person`,
      name: productName,
    },
    publisher: {
      '@type': `Organization`,
      name: productName,
      url: baseUrl,
    },
    datePublished: datePublished,
    dateModified: datePublished,
    image: {
      '@type': `ImageObject`,
      url: fullImageUrl,
      width: 1200,
      height: 630,
    },
    articleSection: `Case Study`,
    inLanguage: localizedConfigData.isoCode || `en`,
  }
}

const CaseStudyPart: FunctionComponent<CaseStudyPartType> = (props) => {
  const date = new Date(props.createdUtc)

  return (
    <div className='pux-repeater-item CasestudyPart-wrapper'>
      <PuxStructuredData structuredData={getCaseStudyJson(props)} />
      <div className='CasestudyPart-item'>
        <PuxLink
          to={`${getLocalizedUrl(props.path)}`}
          className='CasestudyPart-image'
        >
          <PuxMediaSelector {...props.caseStudyImage} />
        </PuxLink>

        <div className='CasestudyPart-info'>
          <div className='CasestudyPart-date'>
            <PuxIcon
              name='user-interface/calendar'
              size={12}
              color='accent'
              decorative
            />
            {date.toLocaleDateString()}
          </div>

          <div className='CasestudyPart-heading'>
            <PuxLink
              className='CasestudyPart-titleLink'
              to={`${getLocalizedUrl(props.path)}`}
            >
              <h3 className='CasestudyPart-title'>{props.displayText}</h3>
            </PuxLink>

            <div className='CasestudyPart-parameters'>
              {props.caseStudyParameters &&
                props.caseStudyParameters.contentItems &&
                props.caseStudyParameters.contentItems.map((parameter, idx) => (
                  <div className='CasestudyPart-parameter' key={idx}>
                    <div className='CasestudyPart-label'>
                      {parameter.displayText}:
                    </div>
                    <PuxWysiwyg
                      wysiwygId={props.contentItemId}
                      inline
                      content={parameter.keyValueContent}
                    />
                  </div>
                ))}
            </div>
          </div>
        </div>

        <div className='CasestudyPart-perex'>
          <PuxWysiwyg
            wysiwygId={props.contentItemId}
            content={props.caseStudyPerex}
            inline
          />
        </div>

        <PuxButton
          buttonType='underline'
          buttonSize='medium'
          buttonIcon='arrows/arrow-right'
          buttonLink={{
            url: [props.path],
            internal: [true],
            text: [t`Redmine.CaseStudy.ReadMore`],
          }}
        />
      </div>
    </div>
  )
}

export default CaseStudyPart
